import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { WorklogService } from '../../services/worklog.service';
import { WorklogFilterService } from '../../services/worklog-filter.service';
import { HelperService } from '../../services/helper.service';
import { ThemeService } from '../../services/theme.service';
import { WorklogResident, WorklogActivity } from '../../models/worklog.model';
import { WorklogChartDeactivationDialogComponent, ChartDeactivationData, ChartDeactivationResult } from '../worklog-chart-deactivation-dialog/worklog-chart-deactivation-dialog.component';
import { WorklogDeleteConfirmationDialogComponent, DeleteConfirmationData } from '../worklog-delete-confirmation-dialog/worklog-delete-confirmation-dialog.component';
import { WorklogDeletionProgressDialogComponent } from '../worklog-deletion-progress-dialog/worklog-deletion-progress-dialog.component';
import { environment } from '../../../environments/environment';

interface ChartInfo {
  has_chart: boolean;
  chart_id?: string;
  chart_type_id?: string;
  customer_code?: string;
  is_active?: boolean;
  status?: number;
}

interface ActivityWithChart extends WorklogActivity {
  chart_info?: ChartInfo;
}

interface ChartGroup {
  chart_id: string | null;
  chart_type_id: string | null;
  is_chart_active: boolean;
  activities: ActivityWithChart[];
  description: string;
}

interface ResidentGroup {
  resident_name: string;
  client_code: string;
  chart_groups: ChartGroup[];
}

interface FlattenedTableRow {
  resident_name: string;
  client_code: string;
  resident_id: string | null;
  chart_id: string | null;
  chart_type_id: string | null;
  is_chart_active: boolean;
  description: string;
  activities: ActivityWithChart[];
}

/**
 * Main Worklog Editor component
 * Now using Material table instead of DHTMLX Scheduler
 */
@Component({
  selector: 'app-worklog-editor',
  templateUrl: './worklog-editor.component.html',
  styleUrls: ['./worklog-editor.component.scss']
})
export class WorklogEditorComponent implements OnInit, OnDestroy {
  isLoading = true;
  currentDate = new Date();
  residentGroups: ResidentGroup[] = [];
  flattenedTableData: FlattenedTableRow[] = [];

  // Mapping of client_code to resident_id for photos
  private residentIdMap = new Map<string, string>();
  private readonly defaultPhotoUrl = 'assets/res-ico.png';
  private readonly photoBaseUrl = 'https://storage.googleapis.com/henley-mobile-app.firebasestorage.app/client_photos';

  // Track failed photo loads to prevent infinite retry loops
  private failedPhotoLoads = new Set<string>();

  private subscriptions: Subscription[] = [];

  constructor(
    private worklogService: WorklogService,
    private worklogFilterService: WorklogFilterService,
    private helperService: HelperService,
    private dialog: MatDialog,
    private themeService: ThemeService
  ) {}

  ngOnInit(): void {
    // Subscribe to filter changes
    const filterSub = this.worklogFilterService.filterState$.subscribe(() => {
      this.applyFilters();
    });
    this.subscriptions.push(filterSub);

    // Load client mapping first, then load worklog data
    this.loadClientMapping();
  }

  /**
   * Load client mapping (Customer_Code -> ResidentID) for photo URLs
   */
  loadClientMapping(): void {
    this.worklogService.getClients().subscribe({
      next: (response) => {
        // Support both response formats (data or clients array)
        const clients = response.data || response.clients || [];

        // Build mapping of Customer_Code to ResidentID
        clients.forEach(client => {
          this.residentIdMap.set(client.Customer_Code, client.ResidentID.toString());
        });

        console.log(`Loaded ${this.residentIdMap.size} client mappings`);

        // Now load worklog data
        this.loadData(this.currentDate);
      },
      error: (error) => {
        console.error('Error loading client mapping:', error);
        // Continue loading worklog even if mapping fails
        this.loadData(this.currentDate);
      }
    });
  }

  loadData(date: Date): void {
    this.isLoading = true;
    this.currentDate = date;

    this.worklogService.getWorklogForDate(date).subscribe({
      next: (response) => {
        if (response.ecase_status_code === 200) {
          this.processResidents(response.data.residents);
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading worklog data:', error);
        this.isLoading = false;
      }
    });
  }

  processResidents(residents: WorklogResident[]): void {
    this.residentGroups = residents.map(resident => {
      // Group activities by chart
      const chartMap = new Map<string, ChartGroup>();

      resident.activities.forEach(activity => {
        const activityWithChart = activity as ActivityWithChart;
        const chartInfo = activityWithChart.chart_info;

        // Create a unique key for grouping
        // Activities with the same chart_id should be grouped together
        // Activities without chart_info should be grouped separately
        const chartKey = chartInfo?.has_chart && chartInfo.chart_id
          ? `chart_${chartInfo.chart_id}`
          : `no_chart_${activity.description}`;

        if (!chartMap.has(chartKey)) {
          chartMap.set(chartKey, {
            chart_id: chartInfo?.chart_id || null,
            chart_type_id: chartInfo?.chart_type_id || null,
            is_chart_active: chartInfo?.is_active || false,
            activities: [],
            description: activity.description
          });
        }

        chartMap.get(chartKey)!.activities.push(activityWithChart);
      });

      return {
        resident_name: resident.resident_name,
        client_code: resident.client_code,
        chart_groups: Array.from(chartMap.values())
      };
    });

    // Build flattened table data for unified table view
    this.flattenedTableData = [];
    this.residentGroups.forEach(resident => {
      resident.chart_groups.forEach(chartGroup => {
        const residentId = this.residentIdMap.get(resident.client_code) || null;

        this.flattenedTableData.push({
          resident_name: resident.resident_name,
          client_code: resident.client_code,
          resident_id: residentId,
          chart_id: chartGroup.chart_id,
          chart_type_id: chartGroup.chart_type_id,
          is_chart_active: chartGroup.is_chart_active,
          description: chartGroup.description,
          activities: chartGroup.activities
        });
      });
    });
  }

  /**
   * Get the photo URL for a resident
   * @param residentId The resident's ID number
   * @returns Photo URL or default avatar
   */
  getResidentPhotoUrl(residentId: string | null): string {
    // If no resident ID or already failed, use default
    if (!residentId || this.failedPhotoLoads.has(residentId)) {
      return this.defaultPhotoUrl;
    }
    return `${this.photoBaseUrl}/${residentId}.jpg`;
  }

  /**
   * Handle image load errors - show default avatar and mark as failed
   * @param event Error event
   */
  handleImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    const currentSrc = img.src;

    // Only set default if not already the default (prevents infinite loop)
    if (!currentSrc.includes('res-ico.png')) {
      // Extract resident ID from the failed URL to mark it
      const match = currentSrc.match(/\/(\d+)\.jpg$/);
      if (match) {
        const residentId = match[1];
        this.failedPhotoLoads.add(residentId);
      }

      img.src = this.defaultPhotoUrl;
    }
  }

  applyFilters(): void {
    // Reload data with current filters
    this.loadData(this.currentDate);
  }

  /**
   * Handle date picker change event
   * @param event MatDatepickerInputEvent
   */
  onDateChange(event: MatDatepickerInputEvent<Date>): void {
    if (event.value) {
      this.loadData(event.value);
    }
  }

  /**
   * Handle delete activities button click
   * @param row FlattenedTableRow containing chart/activity info
   */
  onDeleteActivities(row: FlattenedTableRow): void {
    // If chart exists and is active, show deactivation dialog first
    if (row.chart_id && row.is_chart_active) {
      this.showChartDeactivationDialog(row);
    } else {
      // Proceed directly to deletion confirmation
      this.showDeletionConfirmationDialog(row);
    }
  }

  /**
   * Show chart deactivation warning dialog
   * @param row FlattenedTableRow containing chart/activity info
   */
  private showChartDeactivationDialog(row: FlattenedTableRow): void {
    const dialogData: ChartDeactivationData = {
      chartId: row.chart_id!,
      chartTypeId: row.chart_type_id!,
      customerCode: row.client_code,
      residentName: row.resident_name,
      description: row.description
    };

    const dialogRef = this.dialog.open(WorklogChartDeactivationDialogComponent, {
      width: '500px',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: ChartDeactivationResult) => {
      if (result === 'deactivated') {
        // Chart was successfully deactivated, proceed to deletion
        // Update the row to reflect inactive status
        row.is_chart_active = false;
        this.showDeletionConfirmationDialog(row);
      } else if (result === 'deactivate-in-ecase') {
        // User will deactivate manually in eCase, just return to page
        this.helperService.openSnackBar('Please deactivate the chart in eCase and try again', 'Close');
      }
      // If 'cancelled', do nothing
    });
  }

  /**
   * Show deletion confirmation dialog and handle the deletion process
   * @param row FlattenedTableRow containing chart/activity info
   */
  private showDeletionConfirmationDialog(row: FlattenedTableRow): void {
    // Open dialog immediately with loading state
    const confirmData: DeleteConfirmationData = {
      description: row.description,
      residentName: row.resident_name,
      instanceCount: 0,
      dateRange: '',
      isLoading: true
    };

    const confirmDialogRef = this.dialog.open(WorklogDeleteConfirmationDialogComponent, {
      width: '500px',
      data: confirmData,
      disableClose: true // Prevent closing while loading
    });

    const dialogInstance = confirmDialogRef.componentInstance;

    // Fetch the date range for this client
    this.worklogService.getMaxQueryDate(row.client_code).subscribe({
      next: (response) => {
        const fromDate = this.worklogService.formatDateForApi(new Date());
        const maxQueryDate = response.data.max_query_date;

        // Query all activity instances in the date range to get the accurate count
        this.worklogService.queryActivityInstances(row.client_code, fromDate, maxQueryDate).subscribe({
          next: (queryResponse) => {
            // Count all activities that match this description
            let totalActivityCount = 0;
            queryResponse.data.activity_groups.forEach(group => {
              if (group.description === row.description) {
                totalActivityCount += group.activities.length;
              }
            });

            // Update dialog with loaded data
            dialogInstance.updateData({
              instanceCount: totalActivityCount,
              dateRange: `${fromDate} to ${maxQueryDate}`,
              isLoading: false
            });

            // Re-enable closing now that data is loaded
            confirmDialogRef.disableClose = false;

            confirmDialogRef.afterClosed().subscribe((confirmed: boolean) => {
              if (confirmed) {
                this.performDeletion(row, maxQueryDate);
              }
            });
          },
          error: (error) => {
            console.error('Failed to query activity instances:', error);
            confirmDialogRef.close();
            this.helperService.openSnackBar('Failed to query activity instances', 'Close');
          }
        });
      },
      error: (error) => {
        console.error('Failed to get max query date:', error);
        confirmDialogRef.close();
        this.helperService.openSnackBar('Failed to get date range for deletion', 'Close');
      }
    });
  }

  /**
   * Perform the actual deletion of activity instances
   * @param row FlattenedTableRow containing chart/activity info
   * @param maxQueryDate Maximum query date from API
   */
  private performDeletion(row: FlattenedTableRow, maxQueryDate: string): void {
    const progressDialogRef = this.dialog.open(WorklogDeletionProgressDialogComponent, {
      width: '500px',
      disableClose: true
    });

    const progressDialog = progressDialogRef.componentInstance;

    // Query all activity instances in the date range
    const fromDate = this.worklogService.formatDateForApi(new Date());
    this.worklogService.queryActivityInstances(row.client_code, fromDate, maxQueryDate).subscribe({
      next: (response) => {
        // Extract activity IDs from activity_groups that match this description
        const activityIds: string[] = [];
        response.data.activity_groups.forEach(group => {
          if (group.description === row.description) {
            group.activities.forEach(activity => {
              activityIds.push(activity.activity_id);
            });
          }
        });

        if (activityIds.length === 0) {
          progressDialogRef.close();
          this.helperService.openSnackBar('No activities found to delete', 'Close');
          return;
        }

        progressDialog.updateProgress(0, activityIds.length, 'Starting deletion...');

        let completed = 0;
        let successCount = 0;
        let failureCount = 0;

        // Delete each activity
        activityIds.forEach((activityId: string) => {
          this.worklogService.deleteActivity(activityId).subscribe({
            next: () => {
              completed++;
              successCount++;
              progressDialog.updateProgress(
                completed,
                activityIds.length,
                `Deleting activity ${completed} of ${activityIds.length}`
              );

              if (completed === activityIds.length) {
                progressDialog.setComplete(successCount, failureCount);
                // Reload data after deletion
                setTimeout(() => {
                  this.loadData(this.currentDate);
                }, 2000);
              }
            },
            error: (error) => {
              completed++;
              failureCount++;
              console.error(`Failed to delete activity ${activityId}:`, error);
              progressDialog.updateProgress(
                completed,
                activityIds.length,
                `Deleting activity ${completed} of ${activityIds.length}`
              );

              if (completed === activityIds.length) {
                progressDialog.setComplete(successCount, failureCount);
                // Reload data after deletion
                setTimeout(() => {
                  this.loadData(this.currentDate);
                }, 2000);
              }
            }
          });
        });
      },
      error: (error) => {
        console.error('Failed to query activity instances:', error);
        progressDialogRef.close();
        this.helperService.openSnackBar('Failed to query activity instances', 'Close');
      }
    });
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'Completed': return 'accent';
      case 'Started': return 'primary';
      case 'Not Doing': return 'warn';
      default: return '';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'Completed': return 'check_circle';
      case 'Started': return 'play_circle';
      case 'Not Doing': return 'cancel';
      default: return 'pending';
    }
  }

  /**
   * Get the eCase chart URL for a table row
   * @param row FlattenedTableRow containing chart info
   * @returns eCase chart URL
   */
  getEcaseChartUrl(row: FlattenedTableRow): string {
    if (!row.chart_id || !row.chart_type_id) {
      return '#';
    }

    const currentEnv = this.worklogService.getEnvironment();
    const ecaseBaseUrl = currentEnv === 'uat' ? environment.ecase.uat : environment.ecase.prod;
    return `${ecaseBaseUrl}main.php?action=charts&accessmodule=charting&cid=${row.chart_type_id}&customerCode=${row.client_code}`;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
